/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useCallback, useEffect } from 'react';
import {
  getFileDiff,
  getCommitFiles,
  getCommitFileDiff,
  commitChanges,
  pushCommits,
  revertChanges,
  revertCommitFile,
  createPullRequest,
} from '../../lib/api';
import type { RevertCommitFileResult } from '../../../server/types';
import type { CommitFileInfo } from '../../components/changes';
import type { WorktreeInfo, WorktreeStatus, FileStatus, SourceControlProvider } from '../../components/changes';
import type { SkillShopService } from '../../services/skillShopService';

interface ActiveProduct {
  repositoryUrl?: string | null;
  repoPath?: string | null;
  title?: string;
}

interface GitLabConfig {
  accessToken?: string;
  instanceUrl?: string;
}

interface ToastFn {
  (opts: { title: string; description: string; variant?: 'destructive' }): void;
}

interface UseGitOperationsParams {
  activeProduct: ActiveProduct | null;
  selectedWorktree: WorktreeInfo | null;
  status: WorktreeStatus | null;
  accessToken: string | undefined;
  sourceControlProvider: SourceControlProvider;
  gitlabConfig: GitLabConfig | null;
  defaultBranch: string;
  toast: ToastFn;
  loadStatus: () => Promise<void>;
  loadCommitHistory: () => Promise<void>;
  loadSyncToMainAnalysis: () => Promise<void>;
  skillShopService: SkillShopService;
}

export function useGitOperations({
  activeProduct,
  selectedWorktree,
  status,
  accessToken,
  sourceControlProvider,
  gitlabConfig,
  defaultBranch,
  toast,
  loadStatus,
  loadCommitHistory,
  loadSyncToMainAnalysis,
  skillShopService,
}: UseGitOperationsParams) {
  // File selection state
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [diff, setDiff] = useState<string>('');
  const [selectedFileStatus, setSelectedFileStatus] = useState<FileStatus>('unknown');
  const [isNewFile, setIsNewFile] = useState(false);
  const [isDirectory, setIsDirectory] = useState(false);
  const [directoryFiles, setDirectoryFiles] = useState<Array<{ path: string; name: string; status: FileStatus; staged: boolean }>>([]);
  const [loadingDiff, setLoadingDiff] = useState(false);

  // Commit dialog state
  const [showCommitDialog, setShowCommitDialog] = useState(false);
  const [commitMessage, setCommitMessage] = useState('');
  const [committing, setCommitting] = useState(false);
  const [generatingCommitMessage, setGeneratingCommitMessage] = useState(false);

  // PR dialog state
  const [showPRDialog, setShowPRDialog] = useState(false);
  const [prTitle, setPrTitle] = useState('');
  const [prBody, setPrBody] = useState('');
  const [prBaseBranch, setPrBaseBranch] = useState(defaultBranch);
  const [creatingPR, setCreatingPR] = useState(false);

  // Revert dialog state
  const [showRevertDialog, setShowRevertDialog] = useState(false);
  const [revertAll, setRevertAll] = useState(false);
  const [reverting, setReverting] = useState(false);

  // Push state
  const [pushing, setPushing] = useState(false);

  // Commit files state (for viewing files in a specific commit)
  const [selectedCommitHash, setSelectedCommitHash] = useState<string | null>(null);
  const [selectedCommitFiles, setSelectedCommitFiles] = useState<CommitFileInfo[]>([]);
  const [loadingCommitFiles, setLoadingCommitFiles] = useState(false);

  // Commit file revert state
  const [revertingCommitFile, setRevertingCommitFile] = useState(false);
  const [showCommitFileRevertConflictModal, setShowCommitFileRevertConflictModal] = useState(false);
  const [commitFileRevertConflict, setCommitFileRevertConflict] = useState<RevertCommitFileResult['conflicts'] | null>(null);

  useEffect(() => {
    setPrBaseBranch(defaultBranch);
  }, [defaultBranch]);

  const loadDiff = useCallback(async (filePath: string) => {
    if (!activeProduct?.repositoryUrl || !selectedWorktree) return;

    setLoadingDiff(true);
    setSelectedFile(filePath);
    try {
      const result = await getFileDiff(
        activeProduct.repositoryUrl,
        selectedWorktree.path,
        filePath
      );
      if (!result.success) {
        throw new Error(result.error || 'Failed to load diff');
      }

      if (result.isDirectory) {
        setIsDirectory(true);
        setDirectoryFiles(result.directoryFiles || []);
        setDiff('');
        setSelectedFileStatus('unknown');
        setIsNewFile(false);
      } else {
        setIsDirectory(false);
        setDirectoryFiles([]);
        setDiff(result.diff || '');
        setSelectedFileStatus(result.fileStatus || 'unknown');
        setIsNewFile(result.isNewFile || false);
      }
    } catch (error: any) {
      console.error('Error loading diff:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load diff',
        variant: 'destructive'
      });
      setDiff('');
      setSelectedFileStatus('unknown');
      setIsNewFile(false);
      setIsDirectory(false);
      setDirectoryFiles([]);
    } finally {
      setLoadingDiff(false);
    }
  }, [activeProduct, selectedWorktree, toast]);

  const loadCommitFilesForHash = useCallback(async (commitHash: string) => {
    if (!activeProduct?.repositoryUrl || !selectedWorktree) return;

    setLoadingCommitFiles(true);
    setSelectedCommitHash(commitHash);
    setSelectedCommitFiles([]);
    
    try {
      const result = await getCommitFiles(
        activeProduct.repositoryUrl,
        selectedWorktree.path,
        commitHash
      );
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to load commit files');
      }
      
      setSelectedCommitFiles(result.files || []);
    } catch (error: any) {
      console.error('Error loading commit files:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load commit files',
        variant: 'destructive'
      });
      setSelectedCommitHash(null);
      setSelectedCommitFiles([]);
    } finally {
      setLoadingCommitFiles(false);
    }
  }, [activeProduct, selectedWorktree, toast]);

  const loadCommitFileDiff = useCallback(async (filePath: string, commitHash: string) => {
    if (!activeProduct?.repositoryUrl || !selectedWorktree) return;

    setLoadingDiff(true);
    setSelectedFile(filePath);
    
    try {
      const result = await getCommitFileDiff(
        activeProduct.repositoryUrl,
        selectedWorktree.path,
        commitHash,
        filePath
      );
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to load commit file diff');
      }
      
      setDiff(result.diff || '');
      setSelectedFileStatus(result.fileStatus || 'unknown');
      setIsNewFile(result.fileStatus === 'added');
      setIsDirectory(false);
      setDirectoryFiles([]);
    } catch (error: any) {
      console.error('Error loading commit file diff:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load commit file diff',
        variant: 'destructive'
      });
      setDiff('');
      setSelectedFileStatus('unknown');
      setIsNewFile(false);
    } finally {
      setLoadingDiff(false);
    }
  }, [activeProduct, selectedWorktree, toast]);

  const clearCommitFilesView = useCallback(() => {
    setSelectedCommitHash(null);
    setSelectedCommitFiles([]);
  }, []);

  const handleRevertCommitFile = useCallback(async (filePath: string, commitHash: string) => {
    if (!activeProduct?.repositoryUrl || !selectedWorktree) return;

    setRevertingCommitFile(true);
    setCommitFileRevertConflict(null);

    try {
      const result = await revertCommitFile(
        activeProduct.repositoryUrl,
        selectedWorktree.path,
        commitHash,
        filePath
      );

      if (!result.success) {
        if (result.hasConflicts && result.conflicts) {
          setCommitFileRevertConflict(result.conflicts);
          setShowCommitFileRevertConflictModal(true);
        } else {
          throw new Error(result.error || 'Failed to revert commit file');
        }
        return;
      }

      // First refresh status and commit history
      await loadStatus();
      await loadCommitHistory();

      // Reset the view: exit commit history mode and clear diff
      // This must happen AFTER the API calls complete to avoid stale state
      setSelectedCommitHash(null);
      setSelectedCommitFiles([]);
      setSelectedFile(null);
      setDiff('');
      setSelectedFileStatus('unknown');
      setIsNewFile(false);
      setIsDirectory(false);
      setDirectoryFiles([]);

      toast({
        title: 'Success',
        description: result.message || `Reverted changes to ${filePath}. Click the file to view changes.`
      });
    } catch (error: any) {
      console.error('Error reverting commit file:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to revert commit file',
        variant: 'destructive'
      });
    } finally {
      setRevertingCommitFile(false);
    }
  }, [activeProduct, selectedWorktree, toast, loadStatus, loadCommitHistory]);

  const handleShowCommitDialog = useCallback(async () => {
    if (!activeProduct?.repositoryUrl || !selectedWorktree || !status?.files?.length) {
      setShowCommitDialog(true);
      return;
    }

    const filesToCommit = selectedFiles.size > 0
      ? status.files.filter(f => selectedFiles.has(f.path))
      : status.files;

    if (filesToCommit.length === 0) {
      setShowCommitDialog(true);
      return;
    }

    setGeneratingCommitMessage(true);
    setShowCommitDialog(true);

    try {
      const filesToDiff = filesToCommit.slice(0, 10);
      const diffPromises = filesToDiff.map(async (file) => {
        try {
          const result = await getFileDiff(
            activeProduct.repositoryUrl!,
            selectedWorktree.path,
            file.path
          );
          if (result.success) {
            return `=== ${file.path} ===\n${result.diff || '(no diff available)'}`;
          }
          return `=== ${file.path} ===\n(failed to load diff)`;
        } catch {
          return `=== ${file.path} ===\n(failed to load diff)`;
        }
      });

      const diffs = await Promise.all(diffPromises);
      const combinedDiffs = diffs.join('\n\n');

      const result = await skillShopService.generateCommitMessage(
        filesToCommit.map(f => ({ path: f.path, status: f.status })),
        combinedDiffs
      );

      if (result.success && result.message) {
        setCommitMessage(result.message);
      } else {
        console.warn('[Changes] Failed to generate commit message:', result.error);
      }
    } catch (error) {
      console.error('[Changes] Error generating commit message:', error);
    } finally {
      setGeneratingCommitMessage(false);
    }
  }, [activeProduct, selectedWorktree, status, selectedFiles, skillShopService]);

  const handleCommit = useCallback(async () => {
    if (!activeProduct?.repositoryUrl || !selectedWorktree || !commitMessage.trim()) return;

    setCommitting(true);
    try {
      const filesToCommit = selectedFiles.size > 0 ? Array.from(selectedFiles) : undefined;

      const result = await commitChanges(
        activeProduct.repositoryUrl,
        selectedWorktree.path,
        commitMessage,
        filesToCommit
      );
      if (!result.success) {
        throw new Error(result.error || 'Failed to commit changes');
      }

      toast({
        title: 'Success',
        description: 'Changes committed successfully'
      });

      setShowCommitDialog(false);
      setCommitMessage('');
      await loadStatus();
      await loadCommitHistory();
      await loadSyncToMainAnalysis();
    } catch (error: any) {
      console.error('Error committing changes:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to commit changes',
        variant: 'destructive'
      });
    } finally {
      setCommitting(false);
    }
  }, [activeProduct, selectedWorktree, commitMessage, selectedFiles, toast, loadStatus, loadCommitHistory, loadSyncToMainAnalysis]);

  const handleCreatePR = useCallback(async () => {
    if (!activeProduct?.repositoryUrl || !selectedWorktree || !prTitle.trim() || !accessToken) return;

    setCreatingPR(true);
    try {
      const result = await createPullRequest(
        activeProduct.repositoryUrl,
        selectedWorktree.path,
        prTitle,
        prBody,
        prBaseBranch,
        accessToken,
        sourceControlProvider
      );
      if (!result.success) {
        throw new Error(result.error || 'Failed to create pull request');
      }

      const prLabel = sourceControlProvider === 'github' ? 'Pull request' : 'Merge request';
      toast({
        title: 'Success',
        description: `${prLabel} #${result.pr?.number || result.pr?.iid} created successfully`
      });

      setShowPRDialog(false);
      setPrTitle('');
      setPrBody('');

      if (result.pr?.url || result.pr?.web_url) {
        window.open(result.pr.url || result.pr.web_url, '_blank');
      }
    } catch (error: any) {
      console.error('Error creating PR:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create pull request',
        variant: 'destructive'
      });
    } finally {
      setCreatingPR(false);
    }
  }, [activeProduct, selectedWorktree, prTitle, prBody, prBaseBranch, accessToken, sourceControlProvider, toast]);

  const handleRevert = useCallback(async () => {
    if (!activeProduct?.repositoryUrl || !selectedWorktree) return;

    setReverting(true);
    try {
      const filesToRevert = revertAll ? undefined : Array.from(selectedFiles);

      const result = await revertChanges(
        activeProduct.repositoryUrl,
        selectedWorktree.path,
        filesToRevert
      );
      if (!result.success) {
        throw new Error(result.error || 'Failed to revert changes');
      }

      toast({
        title: 'Success',
        description: revertAll ? 'All changes reverted' : `${selectedFiles.size} file(s) reverted`
      });

      setShowRevertDialog(false);
      setRevertAll(false);
      
      // Clear the diff view and selection since the files have been reverted
      setSelectedFile(null);
      setDiff('');
      setSelectedFileStatus('unknown');
      setIsNewFile(false);
      setIsDirectory(false);
      setDirectoryFiles([]);
      setSelectedFiles(new Set());
      
      await loadStatus();
    } catch (error: any) {
      console.error('Error reverting changes:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to revert changes',
        variant: 'destructive'
      });
    } finally {
      setReverting(false);
    }
  }, [activeProduct, selectedWorktree, revertAll, selectedFiles, toast, loadStatus]);

  const handlePush = useCallback(async () => {
    if (!activeProduct?.repositoryUrl || !selectedWorktree || !accessToken) {
      toast({
        title: 'Error',
        description: `${sourceControlProvider === 'github' ? 'GitHub' : 'GitLab'} access token required for push`,
        variant: 'destructive'
      });
      return;
    }

    setPushing(true);
    try {
      const result = await pushCommits(
        activeProduct.repositoryUrl,
        selectedWorktree.path,
        accessToken,
        sourceControlProvider,
        sourceControlProvider === 'gitlab' ? gitlabConfig?.instanceUrl : undefined
      );
      if (!result.success) {
        throw new Error(result.error || 'Failed to push commits');
      }

      toast({
        title: 'Success',
        description: result.message || 'Commits pushed successfully'
      });

      await loadCommitHistory();
    } catch (error: any) {
      console.error('Error pushing commits:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to push commits',
        variant: 'destructive'
      });
    } finally {
      setPushing(false);
    }
  }, [activeProduct, selectedWorktree, accessToken, sourceControlProvider, gitlabConfig, toast, loadCommitHistory]);

  // File selection handlers
  const toggleFileSelection = useCallback((filePath: string) => {
    const newSelected = new Set(selectedFiles);
    if (newSelected.has(filePath)) {
      newSelected.delete(filePath);
    } else {
      newSelected.add(filePath);
    }
    setSelectedFiles(newSelected);
  }, [selectedFiles]);

  const selectAllFiles = useCallback(() => {
    if (status?.files) {
      setSelectedFiles(new Set(status.files.map(f => f.path)));
    }
  }, [status]);

  const clearSelection = useCallback(() => {
    setSelectedFiles(new Set());
  }, []);

  const clearDiffView = useCallback(() => {
    setSelectedFile(null);
    setDiff('');
    setSelectedFileStatus('unknown');
    setIsNewFile(false);
    setIsDirectory(false);
    setDirectoryFiles([]);
  }, []);

  const clearSelectionsForWorktreeSwitch = useCallback(() => {
    setSelectedFiles(new Set());
    setSelectedFile(null);
    setDiff('');
    setSelectedFileStatus('unknown');
    setIsNewFile(false);
    setIsDirectory(false);
    setDirectoryFiles([]);
    setSelectedCommitHash(null);
    setSelectedCommitFiles([]);
  }, []);

  return {
    // File selection
    selectedFiles,
    setSelectedFiles,
    selectedFile,
    diff,
    selectedFileStatus,
    isNewFile,
    isDirectory,
    directoryFiles,
    loadingDiff,
    toggleFileSelection,
    selectAllFiles,
    clearSelection,
    loadDiff,
    clearDiffView,
    clearSelectionsForWorktreeSwitch,

    // Commit files (viewing files in a specific commit)
    selectedCommitHash,
    selectedCommitFiles,
    loadingCommitFiles,
    loadCommitFilesForHash,
    loadCommitFileDiff,
    clearCommitFilesView,

    // Commit file revert
    revertingCommitFile,
    showCommitFileRevertConflictModal,
    setShowCommitFileRevertConflictModal,
    commitFileRevertConflict,
    handleRevertCommitFile,

    // Commit
    showCommitDialog,
    setShowCommitDialog,
    commitMessage,
    setCommitMessage,
    committing,
    setCommitting,
    generatingCommitMessage,
    handleShowCommitDialog,
    handleCommit,

    // PR
    showPRDialog,
    setShowPRDialog,
    prTitle,
    setPrTitle,
    prBody,
    setPrBody,
    prBaseBranch,
    setPrBaseBranch,
    creatingPR,
    handleCreatePR,

    // Revert
    showRevertDialog,
    setShowRevertDialog,
    revertAll,
    setRevertAll,
    reverting,
    handleRevert,

    // Push
    pushing,
    handlePush,
  };
}
